<template>
  <div class="navbar-end">
    <div class="buttons">
      <!-- Company Switcher — opens the full-screen spotlight (Option+C too) -->
      <!-- In the client portal this lives in the "Viewing as" banner instead. -->
      <div v-if="isNexus && user && (user.type === 'super' || user.type === 'admin') && canImpersonate" class="control">
        <button
          type="button"
          class="nav-circle-btn"
          title="Log into Company (⌥C)"
          aria-label="Log into company"
          @click="openCompanySwitcher"
        >
          <span class="nav-circle">
            <font-awesome-icon :icon="['fal', 'person-to-portal']" />
          </span>
        </button>
      </div>
      <!-- Agency Switcher — same spotlight as the impersonation switcher above,
           but for agency staff. They are ordinary 'user' accounts with real
           membership of each client company, so this is plain navigation, not
           impersonation: no viewModeStore, no theme takeover. -->
      <div v-if="agencyId" class="control">
        <!-- Client portal: the same soft circle as Conversations; elsewhere the classic icon. -->
        <button
          v-if="showClientNav"
          type="button"
          class="nav-circle-btn"
          title="Switch Company"
          aria-label="Switch company"
          @click="isAgencySwitcherOpen = true"
        >
          <span class="nav-circle">
            <font-awesome-icon :icon="['fal', 'building-user']" />
          </span>
        </button>
        <div
          v-else
          class="navbar-icon-wrapper"
          title="Switch Company"
          @click="isAgencySwitcherOpen = true"
        >
          <span class="icon navbar-icon navbar-icon-small has-text-primary">
            <font-awesome-icon :icon="['fad', 'building-user']" />
          </span>
        </div>
      </div>

      <!-- Notifications Bell. The client portal folds it into the user menu. -->
      <div v-if="!bellInMenu" class="control">
        <NotificationBell />
      </div>
      <!-- Conversations (client portal): icon only, unread count badge -->
      <div v-if="showClientNav && clientCanAccessConversations" class="control">
        <router-link
          class="navbar-icon-wrapper conversations-link"
          :class="{ 'is-active': isActiveRoute('account-conversations') }"
          :to="{ name: 'account-conversations', params: { id: currentRouteCompanyId } }"
          :title="conversationsStore.unreadCount ? `Conversations · ${conversationsStore.unreadCount} unread` : 'Conversations'"
          aria-label="Conversations"
        >
          <span class="nav-circle">
            <font-awesome-icon :icon="['fal', 'message-lines']" />
          </span>
          <span v-if="conversationsStore.unreadCount" class="avatar-badge conversations-badge">
            {{ conversationsStore.unreadCount > 9 ? '9+' : conversationsStore.unreadCount }}
          </span>
        </router-link>
      </div>
      <!-- User Profile Dropdown -->
      <div class="control">
        <div
          class="dropdown is-hoverable is-right user-profile-dropdown"
          :class="{ 'is-active': isMenuNotificationsOpen }"
        >
          <!-- Pill trigger: avatar (with its badge) + caret, so it reads as a menu. -->
          <div class="dropdown-trigger avatar-trigger">
            <span class="avatar-pill__avatar">
              <span v-if="bellInMenu && unreadNotificationCount > 0" class="avatar-badge">
                {{ unreadNotificationCount > 9 ? '9+' : unreadNotificationCount }}
              </span>
              <!-- Storage needs attention (moved here with the Company link) -->
              <span
                v-else-if="showClientNav && hasStorageActionRequired"
                class="avatar-dot"
                title="Storage needs attention"
              ></span>
              <!-- Stored avatar URL is a 30-day signed Bunny URL; BunnyImage re-signs it on read -->
              <div v-if="user?.avatar || user?.profile?.avatar" class="navbar-avatar" aria-haspopup="true">
                <BunnyImage
                  :path="user?.profile?.avatarPath || user?.avatarPath"
                  :fallback="user?.profile?.avatar || user?.avatar"
                  alt="Profile"
                  img-class="navbar-avatar-img"
                >
                  <template #placeholder>
                    <span class="icon navbar-icon has-text-success">
                      <font-awesome-icon :icon="['fad', 'circle-user']" />
                    </span>
                  </template>
                </BunnyImage>
              </div>
              <!-- Fallback to user icon -->
              <div v-else class="navbar-icon-wrapper" aria-haspopup="true">
                <span class="icon navbar-icon has-text-success">
                  <font-awesome-icon :icon="['fad', 'circle-user']" />
                </span>
              </div>
            </span>
            <font-awesome-icon :icon="['fas', 'caret-down']" class="avatar-pill__caret" />
          </div>
          <div class="dropdown-menu" id="profile-dropdown-menu" role="menu">
            <div class="dropdown-content">
              <!-- Client portal: the company the user is in, and its details page -->
              <template v-if="showClientNav">
                <component
                  :is="clientCanAccessCompany ? 'router-link' : 'div'"
                  v-bind="clientCanAccessCompany ? { to: { name: 'account-company-details', params: { id: currentRouteCompanyId } } } : {}"
                  class="dropdown-item menu-company"
                  :title="companyName"
                >
                  <span class="menu-company__initials">{{ companyInitials }}</span>
                  <span class="menu-company__text">
                    <span class="menu-company__name">{{ companyName }}</span>
                    <span v-if="clientCanAccessCompany" class="menu-company__sub">Company details</span>
                  </span>
                  <span v-if="hasStorageActionRequired" class="notification-dot menu-company__dot" title="Storage needs attention"></span>
                </component>
                <hr class="dropdown-divider" />
              </template>
              <template v-if="bellInMenu">
                <!-- Flies out to the left like the Feedback submenu: hover opens it,
                     click pins it open (touch, or to keep it while scrolling). -->
                <div
                  class="dropdown-item has-nested-dropdown menu-notifications-item"
                  :class="{ 'is-open': isMenuNotificationsOpen }"
                  @click.stop="isMenuNotificationsOpen = !isMenuNotificationsOpen"
                >
                  <span class="icon has-text-warning">
                    <font-awesome-icon :icon="['fas', 'bell']" />
                  </span>
                  <span>Notifications</span>
                  <span v-if="unreadNotificationCount > 0" class="tag is-danger is-rounded ml-auto menu-count">
                    {{ unreadNotificationCount > 9 ? '9+' : unreadNotificationCount }}
                  </span>
                  <span class="icon is-small" :class="{ 'ml-auto': !unreadNotificationCount }">
                    <font-awesome-icon :icon="['fas', 'chevron-right']" />
                  </span>
                  <div class="nested-dropdown menu-notifications-panel" @click.stop>
                    <NotificationDropdown />
                  </div>
                </div>
                <hr class="dropdown-divider" />
              </template>
              <!-- Multi-company users (not agency staff: their switcher is in the bar). -->
              <template v-if="canSwitchAccount">
                <a class="dropdown-item" @click="handleSwitchAccount">
                  <span class="icon has-text-primary">
                    <font-awesome-icon :icon="['fas', 'arrow-right-arrow-left']" />
                  </span>
                  <span>Switch company</span>
                </a>
                <hr class="dropdown-divider" />
              </template>
              <router-link class="dropdown-item" :to="{ name: profileRouteName }">
                <span class="icon has-text-primary">
                  <font-awesome-icon :icon="['fas', 'user']" />
                </span>
                <span>My Profile</span>
              </router-link>
              <router-link
                v-if="user && (user.type === 'super' || user.type === 'admin')"
                class="dropdown-item"
                :to="{ name: 'admin-my-work', params: { userId: userAuthStore.user?.id || user.id } }"
              >
                <span class="icon has-text-info">
                  <font-awesome-icon :icon="['fas', 'user-check']" />
                </span>
                <span>My Work</span>
              </router-link>
              <!-- Feedback Submenu (Admin only) -->
              <template v-if="isAdmin">
                <hr class="dropdown-divider" />
                <div class="dropdown-item has-nested-dropdown">
                  <span class="icon has-text-purple">
                    <font-awesome-icon :icon="['fas', 'bug']" />
                  </span>
                  <span>Feedback</span>
                  <span class="icon is-small ml-auto">
                    <font-awesome-icon :icon="['fas', 'chevron-right']" />
                  </span>
                  <div class="nested-dropdown">
                    <a class="dropdown-item" @click="handleFeedbackSelect('feature')">
                      <span class="icon has-text-info">
                        <font-awesome-icon :icon="['fas', 'lightbulb']" />
                      </span>
                      <span>Feature Request</span>
                    </a>
                    <a class="dropdown-item" @click="handleFeedbackSelect('bug-page')">
                      <span class="icon has-text-danger">
                        <font-awesome-icon :icon="['fas', 'bug']" />
                      </span>
                      <span>Report a Bug</span>
                    </a>
                  </div>
                </div>
              </template>
              <hr class="dropdown-divider" />
              <a class="dropdown-item" @click="logOut">
                <span class="icon has-text-danger">
                  <font-awesome-icon :icon="['fas', 'arrow-right-from-bracket']" />
                </span>
                <span>Log Out</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <CompanySpotlight
    :open="isAgencySwitcherOpen"
    :companies="agencySwitcherCompanies"
    :current-id="currentAgencyCompanyId"
    @select="handleAgencyCompanySelect"
    @close="isAgencySwitcherOpen = false"
  >
    <template #footer>
      <router-link
        class="button is-small"
        :to="{ name: 'agency-companies', params: { agencyId } }"
        @click="isAgencySwitcherOpen = false"
      >
        <span class="icon">
          <font-awesome-icon :icon="['fas', 'building-user']" />
        </span>
        <span>Agency Portal</span>
      </router-link>
    </template>
  </CompanySpotlight>

  <!-- Feedback Modal -->
  <FeedbackModal
    v-if="showFeedbackModal"
    :feedbackType="feedbackType"
    :selectedElement="selectedElement"
    @close="closeFeedbackModal"
  />
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useNavbarState } from '@/composables/useNavbarState'
import { usePermissions } from '@/composables/usePermissions'
import CompanySpotlight from '@/components/global/nav/CompanySpotlight.vue'
import BunnyImage from '@/components/common/BunnyImage.vue'
import FeedbackModal from '@/components/common/FeedbackModal.vue'
import NotificationBell from '@/components/notifications/NotificationBell.vue'
import NotificationDropdown from '@/components/notifications/NotificationDropdown.vue'
import { useClientPermissions } from '@/composables/useClientPermissions'
import { useConversationsStore } from '@/stores/conversationsStore'
import { getInitials } from '@/utils/userUtils'
import { agencyService } from '@/services/agencyService'
import { logger } from '@/services/loggerService'

const {
  user,
  userAuthStore,
  isNexus,
  isAdmin,
  profileRouteName,
  openCompanySwitcher,
  closeCompanySwitcher,
  isCompanySwitcherOpen,
  handleSwitchAccount,
  logOut,
  unreadNotificationCount,
  showClientNav,
  currentRouteCompanyId,
  companyName,
  hasStorageActionRequired,
  isActiveRoute,
} = useNavbarState()

const { canImpersonate } = usePermissions()

const {
  canAccessCompany: clientCanAccessCompany,
  canAccessConversations: clientCanAccessConversations
} = useClientPermissions()
const conversationsStore = useConversationsStore()

// Multi-company client users switch accounts from the user menu.
const canSwitchAccount = computed(() =>
  !!user.value && user.value.type === 'user' && !agencyId.value && (user.value.companies?.length || 0) > 1
)

// ── Client portal: notifications live in the user menu ──────────
// Nexus and the agency portal keep the bell in the bar.
// Client portal and Nexus fold notifications into the user menu; the agency portal keeps the bell.
const bellInMenu = computed(() => showClientNav.value || isNexus.value)
const isMenuNotificationsOpen = ref(false)
const closeMenuNotifications = (e) => {
  if (e?.target?.closest?.('.user-profile-dropdown')) return
  isMenuNotificationsOpen.value = false
}

// ── Agency switcher ─────────────────────────────────────────────
// Agency staff hold real membership of every client company they can see, so
// moving between them is ordinary navigation. The router's existing
// account-switching guard resets the client stores on the way.
const route = useRoute()
const router = useRouter()

const agencyId = computed(() => userAuthStore.user?.agencyId || null)
const agencyCompanies = ref([])
// Highlights whichever company is currently open; null in the agency portal.
const currentAgencyCompanyId = computed(() => route.params.id || null)

const loadAgencyCompanies = async () => {
  if (!agencyId.value) {
    agencyCompanies.value = []
    return
  }
  try {
    // The agency's own company sits in this list too — it is a Vysual company
    // like any other, so it is not separated out or renamed.
    const roster = await agencyService.getAgencyCompanies(agencyId.value, {
      includeOwn: true,
    })
    // Only companies this user actually has access to — a member scoped to two
    // of them should not see the whole roster in their switcher.
    const mine = new Set(
      (userAuthStore.user?.companies || []).map((c) =>
        typeof c === 'string' ? c : c?.companyId,
      ),
    )
    agencyCompanies.value = roster.filter((c) => mine.has(c.id))
  } catch (error) {
    logger.error('[NavbarUserControls] Failed to load agency companies', {
      error: error.message,
    })
    agencyCompanies.value = []
  }
}

const isAgencySwitcherOpen = ref(false)

// ── Company marker ──────────────────────────────────────────────
const companyInitials = computed(() => getInitials(companyName.value || '') || '?')

// The roster is flat: every company in it belongs to this agency, so none of
// them nest — strip isAgency so the agency's own record is an ordinary row too.
const agencySwitcherCompanies = computed(() =>
  agencyCompanies.value.map((c) => ({ ...c, isAgency: false })),
)

const handleAgencyCompanySelect = (company) => {
  isAgencySwitcherOpen.value = false
  if (!company?.id) return
  router.push({ name: 'account-assets', params: { id: company.id } })
}

watch(agencyId, loadAgencyCompanies, { immediate: true })
watch(() => route.fullPath, () => {
  isMenuNotificationsOpen.value = false
})

// --- Option/Alt + C global shortcut: toggle the company spotlight ---
const handleGlobalKeydown = (e) => {
  // Use e.code (physical key) — on macOS Option+C produces "ç" so e.key is unreliable
  if (!e.altKey || e.metaKey || e.ctrlKey || e.code !== 'KeyC') return

  // Toggle: checked before the form-field guard, since focus sits in the
  // spotlight's own search input while it is open.
  if (isCompanySwitcherOpen.value) {
    e.preventDefault()
    closeCompanySwitcher()
    return
  }

  // Don't hijack typing in form fields
  const target = e.target
  if (
    target &&
    (target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable)
  ) {
    return
  }

  // Mirror the v-if guard on the switcher — only when it's actually rendered
  const currentUser = user?.value
  if (
    !currentUser ||
    !(currentUser.type === 'super' || currentUser.type === 'admin') ||
    !canImpersonate.value
  ) {
    return
  }

  e.preventDefault()
  openCompanySwitcher()
}

onMounted(() => {
  document.addEventListener('keydown', handleGlobalKeydown)
  document.addEventListener('click', closeMenuNotifications)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleGlobalKeydown)
  document.removeEventListener('click', closeMenuNotifications)
})

// Feedback state
const showFeedbackModal = ref(false)
const feedbackType = ref('general')
const selectedElement = ref(null)

const handleFeedbackSelect = (type) => {
  if (type === 'feature') {
    feedbackType.value = 'feature'
    showFeedbackModal.value = true
  } else if (type === 'bug-page') {
    feedbackType.value = 'bug'
    selectedElement.value = {
      tagName: 'BODY',
      selector: 'Full Page',
      text: document.title,
      fullPage: true,
      skipScreenshot: true,
    }
    showFeedbackModal.value = true
  }
}

const closeFeedbackModal = () => {
  showFeedbackModal.value = false
  feedbackType.value = 'general'
  selectedElement.value = null
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/navbar-shared';

.control {
  margin: 0 !important;
}

// Navbar avatar styles
.navbar-avatar {
  width: 32px;
  height: 32px;
  cursor: pointer;

  .navbar-avatar-img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
  }
}

// User profile dropdown adjustments
.user-profile-dropdown {
  .dropdown-menu {
    min-width: 200px;
  }
}

// User menu trigger: a soft pill holding the avatar and a caret.
.avatar-trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 3px 10px 3px 3px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.09);
  }

  .avatar-pill__avatar {
    position: relative; // anchors the unread badge / dot to the avatar
    display: inline-flex;
  }

  .avatar-pill__caret {
    font-size: 0.7rem;
    color: var(--bulma-grey-dark);
  }
}

.user-profile-dropdown:hover .avatar-trigger,
.user-profile-dropdown.is-active .avatar-trigger {
  background: rgba(0, 0, 0, 0.09);
}

.conversations-link {
  position: relative;

  .conversations-badge {
    top: -2px;
    right: -4px;
  }
}

// Right-cluster circle buttons (Conversations, agency switcher): sibling of the
// user-menu pill: same soft fill, no stroke. Sized to the avatar inside the
// pill (not the pill itself) so it doesn't out-weigh it. Glyph is grey-dark for
// >=3:1 contrast.
.nav-circle-btn {
  position: relative;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
}

.conversations-link,
.nav-circle-btn {
  .nav-circle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.05);
    color: var(--bulma-grey-dark);
    font-size: 0.85rem;
    transition: background 0.15s ease, color 0.15s ease;
  }

  &:hover .nav-circle {
    background: rgba(0, 0, 0, 0.09);
    color: var(--bulma-grey-darker);
  }

  // On the Conversations page: tinted with the company's own primary.
  &.is-active .nav-circle {
    background: rgba(var(--bulma-primary-rgb), 0.14);
    color: var(--bulma-primary);
  }
}

.avatar-badge {
  position: absolute;
  top: -4px;
  right: -6px;
  z-index: 1;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: var(--bulma-danger);
  color: #fff;
  font-size: 0.6rem;
  font-weight: 700;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--bulma-white);
  pointer-events: none;
}

.menu-count {
  font-size: 0.65rem;
  height: 1.25em;
  padding: 0 0.4em;
}

// Notifications flyout: same placement as the Feedback submenu, wider, and
// the panel supplies its own padding and chrome.
.menu-notifications-item {
  .menu-notifications-panel.nested-dropdown {
    width: 360px;
    padding: 0;
    overflow: hidden;
    cursor: default;

    :deep(.notification-dropdown) {
      box-shadow: none;
      border-radius: 0;
    }
  }

  &.is-open .menu-notifications-panel.nested-dropdown {
    display: block;
  }
}

// Company header at the top of the client user menu
.menu-company {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  max-width: 280px;

  .menu-company__initials {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(var(--bulma-primary-rgb), 0.12);
    color: var(--bulma-primary);
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.02em;
  }

  .menu-company__text {
    display: flex;
    flex-direction: column;
    min-width: 0;
    line-height: 1.25;
  }

  .menu-company__name {
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .menu-company__sub {
    font-size: 0.75rem;
    color: var(--bulma-text-weak);
  }

  .menu-company__dot {
    position: static;
    margin-left: auto;
    flex-shrink: 0;
  }
}

// Small red dot on the avatar when storage needs attention and there's no count
.avatar-dot {
  position: absolute;
  top: -1px;
  right: -1px;
  z-index: 2;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--bulma-danger);
  border: 2px solid var(--bulma-scheme-main);
}

// Remove dropdown click handlers since we're using hover
.navbar-item-dropdown {
  .dropdown-trigger,
  .dropdown-menu {
    cursor: pointer;
  }
}

.dropdown {
  .dropdown-trigger button {
    border-color: transparent;

    &:focus {
      border-color: var(--bulma-primary);
    }
  }

  .dropdown-menu {
    min-width: 300px;

    .dropdown-content {
      padding: 1rem;

      .dropdown-item {
        padding: 0.5rem;
      }
    }
  }

  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    transition: all 0.2s ease;
    cursor: pointer;

    &:hover {
      background-color: var(--bulma-primary-light);
      color: var(--bulma-primary-dark);
    }

    &.is-active {
      background-color: var(--bulma-primary);
      color: white;

      .icon {
        color: white;
      }
    }

    .icon {
      color: var(--bulma-text);
    }
  }

  .dropdown-divider {
    margin: 0.5rem 0;
  }

  // Nested dropdown submenu styles
  .has-nested-dropdown {
    position: relative;
    cursor: pointer;

    .nested-dropdown {
      display: none;
      position: absolute;
      right: 100%;
      left: auto;
      top: -0.5rem;
      min-width: 200px;
      background: var(--bulma-white);
      border-radius: 6px;
      box-shadow: 0 8px 16px rgba(10, 10, 10, 0.1);
      padding: 0.5rem 0;
      z-index: 60;

      .dropdown-item {
        padding: 0.5rem 1rem;
        white-space: nowrap;

        &:hover {
          background-color: var(--bulma-primary-light);
          color: var(--bulma-primary-dark);
        }
      }
    }

    &:hover .nested-dropdown {
      display: block;
    }
  }
}
</style>
